import { flow, types } from 'mobx-state-tree';
import * as Sentry from '@sentry/browser';
import { PlanningActionMetadataDtoModel, PlanningActionsDtoModelType } from './models';
import planningActionQuery from './queries/planningActions/PlanningActionQuery';
import planningActionsQuery from './queries/planningActions/PlanningActionsQuery';
import getAppRoot from './utils/getAppRoot';
import ActivePlanningActionQuery from './queries/planningActions/ActivePlanningActionQuery';

export enum ActionType {
  optimizeOrdersToTargetRoutes = 'OptimizeOrdersToTargetRoutes',
  moveOrdersToRoutes = 'MoveOrdersToRoutes',
  uploadOrders = 'UploadOrders',
  addedSingleOrder = 'AddedSingleOrder',
}

const PlanningActions = types.model('PlanningActions')
  .props({
    lastUndoablePlanningAction: types.maybeNull(PlanningActionMetadataDtoModel),
    nextRedoablePlanningAction: types.maybeNull(PlanningActionMetadataDtoModel),
    isLoading: types.optional(types.boolean, false),
    isUndoing: types.optional(types.boolean, false),
    isRedoing: types.optional(types.boolean, false),
    fetchAllRoutesActionUuids: types.optional(types.map(types.literal(true)), {}),
    scheduleAllActionUuids: types.optional(types.map(types.literal(true)), {}),
    scaffoldRoutesActionUuids: types.optional(types.map(types.literal(true)), {}),
  })
  .views((self) => ({
    get hasLastUndoablePlanningAction(): boolean {
      return !!self.lastUndoablePlanningAction;
    },
    get hasNextRedoablePlanningAction(): boolean {
      return !!self.nextRedoablePlanningAction;
    },
    fetchForActionUuid(actionUuid: string): boolean {
      return self.fetchAllRoutesActionUuids.has(actionUuid);
    },
    scheduleAllForActionUuid(actionUuid: string): boolean {
      return self.scheduleAllActionUuids.has(actionUuid);
    },
    scaffoldRoutesForActionUuid(actionUuid: string): boolean {
      return self.scaffoldRoutesActionUuids.has(actionUuid);
    },
    get isScaffoldingRoutes(): boolean {
      return self.scaffoldRoutesActionUuids.size > 0;
    },
  }))
  .actions((self) => ({
    getActivePlanningActions: flow(function* getActivePlanningActions() {
      const fetchActivePlanningActions = async (): Promise<PlanningActionsDtoModelType[] | undefined> => {
        const selectedWorkspaceId = getAppRoot(self).ui?.selectedWorkspace?.id;

        if (selectedWorkspaceId) {
          try {
            const response = await getAppRoot(self).model.queryActivePlanningActions({
              projectId: selectedWorkspaceId,
            }, ActivePlanningActionQuery, { fetchPolicy: 'no-cache' }).promise;
            return response.activePlanningActions.actions;
          } catch (err) {
            Sentry.captureException(err, {
              level: 'warning',
              tags: {
                feature: 'planning-actions',
                action: 'getActivePlanningActions',
              },
              extra: {
                projectId: selectedWorkspaceId,
                message: 'Failed to fetch active planning actions',
              },
            });
            return;
          }
        }
      };

      self.isLoading = true;
      const actions = yield fetchActivePlanningActions();
      self.isLoading = false;

      return actions || [];
    }),
    getUndoableRedoableActions: flow(function* getUndoableRedoableActions(undoRedo?: boolean | null) {
      const fetchUndoableRedoablePlanningActions = async (): Promise<any> => {
        const selectedWorkspaceId = getAppRoot(self).ui?.selectedWorkspace?.id;

        if (selectedWorkspaceId) {
          try {
            // TODO: Rename this resolver on the backend to be more descriptive
            const response = await getAppRoot(self).model.queryPlanningActions({
              projectId: selectedWorkspaceId,
            }, planningActionsQuery(!!undoRedo), { fetchPolicy: 'no-cache' }).promise;
            return await Promise.resolve(response.planningActions);
          } catch (err) {
            return Promise.resolve(undefined);
          }
        }

        return Promise.resolve(undefined);
      };

      self.isLoading = true;
      const requestResponse = yield fetchUndoableRedoablePlanningActions();

      if (requestResponse) {
        const { lastUndoablePlanningAction, nextRedoablePlanningAction } = requestResponse;
        self.lastUndoablePlanningAction = lastUndoablePlanningAction ? { ...lastUndoablePlanningAction } : null;
        self.nextRedoablePlanningAction = nextRedoablePlanningAction ? { ...nextRedoablePlanningAction } : null;
      }

      self.isLoading = false;
      self.isUndoing = false;
      self.isRedoing = false;
    }),
    undo: flow(function* undo() {
      const undoAction = async (): Promise<any> => {
        const selectedWorkspaceId = getAppRoot(self).ui?.selectedWorkspace?.id;

        if (selectedWorkspaceId) {
          try {
            await getAppRoot(self).model.mutateUndo({
              projectId: selectedWorkspaceId,
            }, planningActionQuery).promise;
            return await Promise.resolve();
          } catch (err) {
            return Promise.reject(err);
          }
        }

        return Promise.resolve(undefined);
      };

      self.isLoading = true;
      self.isUndoing = true;
      try {
        yield undoAction();
      } catch (err) {
        self.isLoading = false;
        self.isUndoing = false;
        throw (err);
      }

      self.lastUndoablePlanningAction = null;
    }),
    redo: flow(function* redo() {
      const redoAction = async (): Promise<any> => {
        const selectedWorkspaceId = getAppRoot(self).ui?.selectedWorkspace?.id;

        if (selectedWorkspaceId) {
          try {
            await getAppRoot(self).model.mutateRedo({
              projectId: selectedWorkspaceId,
            }, planningActionQuery).promise;
            return await Promise.resolve();
          } catch (err) {
            return Promise.reject(err);
          }
        }

        return Promise.resolve(undefined);
      };

      self.isLoading = true;
      self.isRedoing = true;
      try {
        yield redoAction();
      } catch (err) {
        self.isLoading = false;
        self.isRedoing = false;
        throw (err);
      }

      self.nextRedoablePlanningAction = null;
    }),
    setIsLoading(isLoading: boolean) {
      self.isLoading = isLoading;
    },
    trackFetchAllRoutesAction(actionUuid: string) {
      self.fetchAllRoutesActionUuids.set(actionUuid, true);
    },
    clearFetchAllRoutesAction(actionUuid: string) {
      self.fetchAllRoutesActionUuids.delete(actionUuid);
    },
    trackScheduleAllAction(actionUuid: string) {
      self.scheduleAllActionUuids.set(actionUuid, true);
    },
    clearScheduleAllAction(actionUuid: string) {
      self.scheduleAllActionUuids.delete(actionUuid);
    },
    trackScaffoldRoutesAction(actionUuid: string) {
      self.scaffoldRoutesActionUuids.set(actionUuid, true);
    },
    clearScaffoldRoutesAction(actionUuid: string) {
      self.scaffoldRoutesActionUuids.delete(actionUuid);
    },
    setMapBoundsBasedOnAction(
      latestCompletedAction: { actionName?: string | null },
      { forceFit = false }: { forceFit?: boolean } = {},
    ) {
      // MAP PANNING
      // This action is designed to centralize map panning logic
      // It allows us to explicity pan the map when a specific action and its side effects have finished
      // MST views are not bound, so call them on the store rather than
      // destructuring them (areBoundsPartiallyVisible relies on `this`).
      const { mapView, allOrdersBounds } = getAppRoot(self);

      switch (latestCompletedAction.actionName) {
        case ActionType.optimizeOrdersToTargetRoutes:
        case ActionType.moveOrdersToRoutes:
          // Stop moves usually come from a drag on the map, timeline or route
          // panel, where the user is already looking at the right area. Only
          // refit when the day's orders are completely off screen. A fresh
          // optimization (schedule all, or routes scaffolded from templates)
          // builds routes the user hasn't seen yet, so it always fits the day.
          if (forceFit || !allOrdersBounds || !mapView.areBoundsPartiallyVisible(allOrdersBounds)) {
            mapView.setMapPosition(allOrdersBounds);
          }
          break;
        case ActionType.uploadOrders:
        case ActionType.addedSingleOrder:
          mapView.setMapPosition(allOrdersBounds);
          break;
        // TODO: Add more cases here as needed if needing to be more specific in what we pan to
        default:
          break;
      }
    },
  }));

export default PlanningActions;
